import { parseMapDocument, serializeMap, type MapDocument } from '../../sim/map/index.ts';
import { sanitizeMapFilename } from '../../sim/map/filename.ts';

export { sanitizeMapFilename } from '../../sim/map/filename.ts';

const STORAGE_KEY = 'battleboats:editor:map';
const FILE_NAME_KEY = 'battleboats:editor:file';
const DIRTY_KEY = 'battleboats:editor:dirty';
const DIRTY_VALUE = '1';
const REPO_SAVE_PATH = '/__battleboats/save-map';
const REPO_DELETE_PATH = '/__battleboats/delete-map';
export const DEFAULT_EXPORT_FILENAME = 'battleboats-map.json';
export const UNTITLED_MAP_NAME = 'Untitled';

/** Stem shown in the menubar: `one-stone` from `one-stone.json`, or Untitled. */
export function mapFileDisplayStem(fileName: string): string {
  if (fileName === UNTITLED_MAP_NAME) {
    return UNTITLED_MAP_NAME;
  }

  return fileName.replace(/\.json$/i, '');
}

const MAP_PICKER_TYPES: FilePickerAcceptType[] = [
  { description: 'Battleboats map', accept: { 'application/json': ['.json'] } },
];

export type ResumableDraft = {
  map: MapDocument;
  fileName: string | null;
};

/** localStorage access is wrapped in try/catch since it can throw (quota, privacy mode, disabled). */
export function saveToLocalStorage(map: MapDocument): void {
  try {
    localStorage.setItem(STORAGE_KEY, serializeMap(map));
  } catch {
    // Ignore storage failures; autosave is best-effort.
  }
}

export function loadFromLocalStorage(): MapDocument | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? parseMapDocument(raw) : null;
  } catch {
    return null;
  }
}

/**
 * Crash-recovery snapshot. Only a dirty working copy is resumable — a clean save or an
 * untouched catalog map must not offer "Resume editing?" on the next visit.
 */
export function saveResumableDraft(map: MapDocument): void {
  saveToLocalStorage(map);
  try {
    localStorage.setItem(DIRTY_KEY, DIRTY_VALUE);
  } catch {
    // Ignore storage failures; autosave is best-effort.
  }
}

export function clearResumableDraft(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(DIRTY_KEY);
  } catch {
    // Ignore storage failures; autosave is best-effort.
  }
}

/** Present only when the last editor session left unsaved changes. */
export function loadResumableDraft(): ResumableDraft | null {
  try {
    if (localStorage.getItem(DIRTY_KEY) !== DIRTY_VALUE) {
      return null;
    }

    const map = loadFromLocalStorage();
    return map ? { map, fileName: loadRepoFileName() } : null;
  } catch {
    return null;
  }
}

export function exportMapFile(map: MapDocument, filename = DEFAULT_EXPORT_FILENAME): void {
  const blob = new Blob([serializeMap(map)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

/** Filename shown on the menubar: the bound file, else an import name, else Untitled. */
export function mapFileDisplayName(
  handle: FileSystemFileHandle | null,
  fallback: string | null = null,
): string {
  return handle?.name || fallback || UNTITLED_MAP_NAME;
}

export function saveRepoFileName(name: string | null): void {
  try {
    if (name) {
      localStorage.setItem(FILE_NAME_KEY, name);
    } else {
      localStorage.removeItem(FILE_NAME_KEY);
    }
  } catch {
    // Ignore storage failures; the in-memory name is enough for this page.
  }
}

export function loadRepoFileName(): string | null {
  try {
    const raw = localStorage.getItem(FILE_NAME_KEY);
    return raw ? sanitizeMapFilename(raw) : null;
  } catch {
    return null;
  }
}

/** Vite DEV middleware can write `maps/*.json`. Preview and production cannot. */
export function canWriteRepoMap(): boolean {
  return import.meta.env.DEV;
}

export async function writeRepoMap(filename: string, contents: string): Promise<boolean> {
  const name = sanitizeMapFilename(filename);

  if (!name) {
    return false;
  }

  try {
    const response = await fetch(REPO_SAVE_PATH, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ filename: name, contents }),
    });
    return response.ok;
  } catch {
    return false;
  }
}

/** Bound catalog file that DEV Save / Delete can touch. Untitled and imports are not. */
export function boundRepoFilename(repoFile: string | null): string | null {
  return repoFile ? sanitizeMapFilename(repoFile) : null;
}

/** File → Delete only works in DEV against a bound `maps/<name>.json`. */
export function canDeleteRepoMap(repoFile: string | null): boolean {
  return canWriteRepoMap() && boundRepoFilename(repoFile) !== null;
}

/**
 * Unlinks `maps/<filename>` through the DEV Vite hook.
 * A missing file still counts as gone (404) so the editor can reset.
 */
export async function deleteRepoMap(filename: string): Promise<boolean> {
  const name = sanitizeMapFilename(filename);

  if (!name) {
    return false;
  }

  try {
    const response = await fetch(REPO_DELETE_PATH, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ filename: name }),
    });
    return response.ok || response.status === 404;
  } catch {
    return false;
  }
}

export type PickMapJsonResult =
  | { status: 'picked'; json: string; fileName: string }
  | { status: 'cancelled' }
  | { status: 'unreadable' };

export const REFERENCE_IMAGE_MAX_BYTES = 20 * 1024 * 1024;
const REFERENCE_IMAGE_ACCEPT = 'image/png,image/jpeg,image/webp';

export type PickImageFileResult =
  | { status: 'picked'; file: File; fileName: string }
  | { status: 'cancelled' }
  | { status: 'too-large' };

/** Native raster picker for the editor tracing overlay. Decode happens in the scene. */
export function pickImageFile(): Promise<PickImageFileResult> {
  return new Promise((resolve) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = REFERENCE_IMAGE_ACCEPT;
    input.style.display = 'none';
    let settled = false;

    const finish = (value: PickImageFileResult): void => {
      if (settled) {
        return;
      }

      settled = true;
      input.remove();
      resolve(value);
    };

    input.addEventListener('change', () => {
      const file = input.files?.[0];

      if (!file) {
        finish({ status: 'cancelled' });
        return;
      }

      if (file.size > REFERENCE_IMAGE_MAX_BYTES) {
        finish({ status: 'too-large' });
        return;
      }

      finish({ status: 'picked', file, fileName: file.name });
    });
    input.addEventListener('cancel', () => {
      finish({ status: 'cancelled' });
    });

    document.body.append(input);
    input.click();
  });
}

/** Native JSON file picker. Parse happens in `importMapDocument`, not here. */
export function pickMapJsonFile(): Promise<PickMapJsonResult> {
  return new Promise((resolve) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'application/json';
    input.style.display = 'none';
    let settled = false;

    const finish = (value: PickMapJsonResult): void => {
      if (settled) {
        return;
      }

      settled = true;
      input.remove();
      resolve(value);
    };

    input.addEventListener('change', () => {
      const file = input.files?.[0];

      if (!file) {
        finish({ status: 'cancelled' });
        return;
      }

      const reader = new FileReader();

      reader.onload = () => {
        const json = typeof reader.result === 'string' ? reader.result : '';
        finish({ status: 'picked', json, fileName: file.name });
      };
      reader.onerror = () => {
        finish({ status: 'unreadable' });
      };
      reader.readAsText(file);
    });
    input.addEventListener('cancel', () => {
      finish({ status: 'cancelled' });
    });

    document.body.append(input);
    input.click();
  });
}

export function supportsSavePicker(): boolean {
  return typeof globalThis.showSaveFilePicker === 'function';
}

export function supportsOpenPicker(): boolean {
  return typeof globalThis.showOpenFilePicker === 'function';
}

/** True when the browser supports native save/open dialogs (File System Access API, Chromium-based browsers). */
export function supportsFilePicker(): boolean {
  return supportsSavePicker() && supportsOpenPicker();
}

function isAbortError(error: unknown): boolean {
  return error instanceof DOMException && error.name === 'AbortError';
}

export type SaveMapResult =
  | { status: 'saved'; handle: FileSystemFileHandle | null; repoFile: string | null }
  | { status: 'cancelled' }
  | { status: 'error' };

export type SaveMapOptions = {
  /** Always open the picker and bind the chosen file, even when `handle` is already set. */
  saveAs?: boolean;
};

export type SaveMapRequest = {
  map: MapDocument;
  handle: FileSystemFileHandle | null;
  repoFile: string | null;
  saveAs: boolean;
  /** In-app name prompt. Used in DEV when the OS picker cannot silently rewrite. */
  pickRepoFile: (suggested: string) => Promise<string | null>;
};

async function writeToHandle(handle: FileSystemFileHandle, contents: string): Promise<boolean> {
  try {
    const query = handle.queryPermission?.bind(handle);
    const request = handle.requestPermission?.bind(handle);
    const current = query ? await query({ mode: 'readwrite' }) : 'granted';

    if (current !== 'granted' && request) {
      const next = await request({ mode: 'readwrite' });

      if (next !== 'granted') {
        return false;
      }
    }

    const writable = await handle.createWritable();
    await writable.write(contents);
    await writable.close();
    return true;
  } catch {
    return false;
  }
}

/**
 * DEV writes `maps/<name>.json` through Vite so Save is silent after the first name.
 * Preview/production still use File System Access (or a download) because that middleware
 * is not available.
 */
export async function saveMap(request: SaveMapRequest): Promise<SaveMapResult> {
  const contents = (): string => serializeMap(request.map);
  const suggested = request.repoFile ?? request.handle?.name ?? DEFAULT_EXPORT_FILENAME;

  if (!request.saveAs && request.repoFile && canWriteRepoMap()) {
    const ok = await writeRepoMap(request.repoFile, contents());
    return ok
      ? { status: 'saved', handle: request.handle, repoFile: request.repoFile }
      : { status: 'error' };
  }

  if (!request.saveAs && request.handle && supportsSavePicker()) {
    const ok = await writeToHandle(request.handle, contents());

    if (ok) {
      return {
        status: 'saved',
        handle: request.handle,
        repoFile: request.repoFile ?? request.handle.name,
      };
    }
  }

  if (canWriteRepoMap() && (request.saveAs || !request.repoFile)) {
    const name = await request.pickRepoFile(suggested);

    if (!name) {
      return { status: 'cancelled' };
    }

    const ok = await writeRepoMap(name, contents());
    return ok ? { status: 'saved', handle: request.handle, repoFile: name } : { status: 'error' };
  }

  return saveMapWithDialog(request.map, request.handle, { saveAs: request.saveAs });
}

/**
 * Writes the map through the File System Access API. Save reuses `handle` when present so a
 * later click does not re-prompt; Save As always opens the native picker. Unsupported browsers
 * fall back to an anonymous download.
 */
export async function saveMapWithDialog(
  map: MapDocument,
  handle: FileSystemFileHandle | null,
  options: SaveMapOptions = {},
): Promise<SaveMapResult> {
  const suggestedName = handle?.name || DEFAULT_EXPORT_FILENAME;

  if (!supportsSavePicker()) {
    exportMapFile(map, suggestedName);
    return { status: 'saved', handle: null, repoFile: sanitizeMapFilename(suggestedName) };
  }

  try {
    const target =
      handle && !options.saveAs
        ? handle
        : await globalThis.showSaveFilePicker({
            suggestedName,
            types: MAP_PICKER_TYPES,
          });
    const ok = await writeToHandle(target, serializeMap(map));
    return ok
      ? { status: 'saved', handle: target, repoFile: target.name }
      : { status: 'error' };
  } catch (error) {
    return isAbortError(error) ? { status: 'cancelled' } : { status: 'error' };
  }
}

export type OpenMapResult =
  | { status: 'opened'; map: MapDocument; handle: FileSystemFileHandle }
  | { status: 'cancelled' }
  | { status: 'error' };

/** Opens a map through a native "Open" dialog. Callers should fall back to `pickMapJsonFile` when unsupported. */
export async function openMapWithDialog(): Promise<OpenMapResult> {
  if (!supportsOpenPicker()) {
    return { status: 'error' };
  }

  try {
    const [handle] = await globalThis.showOpenFilePicker({ types: MAP_PICKER_TYPES });
    const file = await handle.getFile();
    const text = await file.text();
    const map = parseMapDocument(text);

    return map ? { status: 'opened', map, handle } : { status: 'error' };
  } catch (error) {
    return isAbortError(error) ? { status: 'cancelled' } : { status: 'error' };
  }
}
